import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { GripVertical, Trash2, Image as ImageIcon } from "lucide-react";
import { DndContext, closestCenter, PointerSensor, KeyboardSensor, useSensor, useSensors } from "@dnd-kit/core";
import { SortableContext, sortableKeyboardCoordinates, verticalListSortingStrategy, useSortable, arrayMove } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

const TYPE_LABELS = {
  paragraph: "Параграф", heading2: "Подзаглавие (H2)", heading3: "Подзаглавие (H3)",
  quote: "Цитат", bullet_list: "Списък с точки", numbered_list: "Номериран списък",
  image: "Изображение", youtube_embed: "YouTube видео",
};

function genKey() {
  return typeof crypto !== "undefined" && crypto.randomUUID ? crypto.randomUUID() : `${Date.now()}-${Math.random()}`;
}

function SortableBlockCard({ id, index, label, onDelete, children }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });
  const style = { transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.5 : 1 };

  return (
    <div ref={setNodeRef} style={style} className="border border-[#E2E8F0] bg-white p-4" data-testid={`block-${index}`}>
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <button
            type="button"
            {...attributes}
            {...listeners}
            className="cursor-grab active:cursor-grabbing touch-none text-[#718096] hover:text-[#1A1A1A]"
            data-testid={`block-${index}-drag-handle`}
          >
            <GripVertical className="w-4 h-4" />
          </button>
          <span className="text-xs font-bold uppercase tracking-wide text-[#A6192E]">{label}</span>
        </div>
        <button type="button" onClick={onDelete} data-testid={`block-${index}-delete`}>
          <Trash2 className="w-4 h-4 text-red-500" />
        </button>
      </div>
      {children}
    </div>
  );
}

export function BlockEditor({ blocks, onChange, onPickImage }) {
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const update = (i, patch) => {
    const next = blocks.slice();
    next[i] = { ...next[i], ...patch };
    onChange(next);
  };

  const remove = (i) => onChange(blocks.filter((_, idx) => idx !== i));

  const addBlock = (type) => {
    const base = { type, key: genKey() };
    if (type === "bullet_list" || type === "numbered_list") base.items = [];
    onChange([...blocks, base]);
  };

  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = blocks.findIndex((b) => b.key === active.id);
    const newIndex = blocks.findIndex((b) => b.key === over.id);
    if (oldIndex === -1 || newIndex === -1) return;
    onChange(arrayMove(blocks, oldIndex, newIndex));
  };

  return (
    <div className="space-y-4" data-testid="block-editor">
      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
        <SortableContext items={blocks.map((b) => b.key)} strategy={verticalListSortingStrategy}>
          {blocks.map((b, i) => (
            <SortableBlockCard key={b.key} id={b.key} index={i} label={TYPE_LABELS[b.type] || b.type} onDelete={() => remove(i)}>
              {["paragraph", "heading2", "heading3", "quote"].includes(b.type) && (
                <Textarea value={b.text || ""} onChange={(e) => update(i, { text: e.target.value })} placeholder="Текст (поддържа **bold** и [линк](url))" data-testid={`block-${i}-text-input`} />
              )}

              {["bullet_list", "numbered_list"].includes(b.type) && (
                <Textarea
                  value={(b.items || []).join("\n")}
                  onChange={(e) => update(i, { items: e.target.value.split("\n") })}
                  placeholder="Всеки елемент на нов ред"
                  data-testid={`block-${i}-items-input`}
                />
              )}

              {b.type === "image" && (
                <div className="space-y-2">
                  {b.image_url ? (
                    <img src={b.image_url} alt="" className="w-full max-w-md aspect-video object-cover" />
                  ) : (
                    <div className="w-full max-w-md aspect-video bg-[#F3F4F6] flex items-center justify-center text-[#718096]"><ImageIcon className="w-6 h-6" /></div>
                  )}
                  <Button type="button" size="sm" variant="outline" onClick={() => onPickImage((url) => update(i, { image_url: url }))} data-testid={`block-${i}-pick-image-btn`}>
                    Изберете изображение
                  </Button>
                  <Input value={b.alt_text || ""} onChange={(e) => update(i, { alt_text: e.target.value })} placeholder="Alt текст" data-testid={`block-${i}-alt-input`} />
                  <Input value={b.caption || ""} onChange={(e) => update(i, { caption: e.target.value })} placeholder="Caption (по избор)" data-testid={`block-${i}-caption-input`} />
                </div>
              )}

              {b.type === "youtube_embed" && (
                <Input value={b.youtube_url || ""} onChange={(e) => update(i, { youtube_url: e.target.value })} placeholder="YouTube линк" data-testid={`block-${i}-youtube-input`} />
              )}
            </SortableBlockCard>
          ))}
        </SortableContext>
      </DndContext>

      <div className="flex flex-wrap gap-2 pt-2">
        {Object.entries(TYPE_LABELS).map(([type, label]) => (
          <Button key={type} type="button" size="sm" variant="outline" onClick={() => addBlock(type)} data-testid={`add-block-${type}`}>
            + {label}
          </Button>
        ))}
      </div>
    </div>
  );
}
